import {
  CircleHelp,
  Copy,
  Ellipsis,
  Folder,
  Import,
  PanelTop,
  Plus,
  Search,
  Settings,
  Trash2,
  Waypoints,
} from "lucide-react";
import { useState, type CSSProperties, type DragEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";

export interface SidebarProject {
  id: string;
  title: string;
  boardCount?: number;
}

export interface SidebarBoard {
  id: string;
  title: string;
  projectId?: string;
  parentId?: string | null;
  nodeCount?: number;
}

export interface SidebarProps {
  projects: readonly SidebarProject[];
  boards: readonly SidebarBoard[];
  activeProjectId: string | null;
  activeBoardId: string | null;
  query: string;
  onQueryChange: (query: string) => void;
  onSelectProject: (projectId: string) => void;
  onSelectBoard: (boardId: string) => void;
  onMoveBoard?: (boardId: string, parentId: string | null) => void;
  onDuplicateBoard?: (boardId: string) => void;
  onRequestMoveBoard?: (boardId: string) => void;
  onRequestDeleteBoard?: (boardId: string) => void;
  onCreateProject: () => void;
  onCreateBoard: () => void;
  onImportProject?: () => void;
  onOpenSettings?: () => void;
  onOpenHelp?: () => void;
  disabled?: boolean;
}

function matchesQuery(value: string, query: string): boolean {
  return value.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
}

const BOARD_DRAG_MIME = "application/x-kimnote-board";

function flattenBoardTree(boards: readonly SidebarBoard[]): Array<{ board: SidebarBoard; depth: number }> {
  const ids = new Set(boards.map((board) => board.id));
  const children = new Map<string | null, SidebarBoard[]>();
  for (const board of boards) {
    const parentId = board.parentId && ids.has(board.parentId) ? board.parentId : null;
    children.set(parentId, [...(children.get(parentId) ?? []), board]);
  }
  const rows: Array<{ board: SidebarBoard; depth: number }> = [];
  const visited = new Set<string>();
  const visit = (board: SidebarBoard, depth: number) => {
    if (visited.has(board.id)) return;
    visited.add(board.id);
    rows.push({ board, depth });
    for (const child of children.get(board.id) ?? []) visit(child, depth + 1);
  };
  for (const root of children.get(null) ?? []) visit(root, 0);
  for (const board of boards) visit(board, 0);
  return rows;
}

export function Sidebar({
  projects,
  boards,
  activeProjectId,
  activeBoardId,
  query,
  onQueryChange,
  onSelectProject,
  onSelectBoard,
  onMoveBoard,
  onDuplicateBoard,
  onRequestMoveBoard,
  onRequestDeleteBoard,
  onCreateProject,
  onCreateBoard,
  onImportProject,
  onOpenSettings,
  onOpenHelp,
  disabled = false,
}: SidebarProps) {
  const [boardMenu, setBoardMenu] = useState<{ board: SidebarBoard; top: number; left: number } | null>(null);
  const visibleProjects = query
    ? projects.filter((project) => matchesQuery(project.title, query))
    : projects;
  const projectBoards = boards.filter(
    (board) => !board.projectId || !activeProjectId || board.projectId === activeProjectId,
  );
  const visibleBoards = query
    ? projectBoards.filter((board) => matchesQuery(board.title, query))
    : projectBoards;
  const boardRows = flattenBoardTree(visibleBoards);
  const hasFooter = Boolean(onImportProject || onOpenSettings || onOpenHelp);

  const beginBoardDrag = (event: DragEvent<HTMLDivElement>, boardId: string) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(BOARD_DRAG_MIME, boardId);
    event.dataTransfer.setData("text/plain", boardId);
  };

  const acceptBoardDrop = (event: DragEvent, parentId: string | null) => {
    const boardId = event.dataTransfer.getData(BOARD_DRAG_MIME);
    if (!boardId || boardId === parentId) return;
    event.preventDefault();
    event.stopPropagation();
    onMoveBoard?.(boardId, parentId);
  };

  const toggleBoardMenu = (event: MouseEvent<HTMLButtonElement>, board: SidebarBoard) => {
    event.stopPropagation();
    if (boardMenu?.board.id === board.id) {
      setBoardMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setBoardMenu({
      board,
      top: Math.min(rect.bottom + 5, window.innerHeight - 126),
      left: Math.max(8, Math.min(rect.right - 148, window.innerWidth - 156)),
    });
  };

  return (
    <>
    <aside id="project-sidebar" className="sidebar" aria-label="项目与画板">
      <label className="sidebar-search">
        <Search size={14} aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.currentTarget.value)}
          placeholder="搜索项目与画板"
          aria-label="搜索项目与画板"
          disabled={disabled}
        />
      </label>

      <section className="sidebar-section" aria-labelledby="projects-heading">
        <div className="sidebar-heading" id="projects-heading">
          <span>项目</span>
          <button
            type="button"
            className="sidebar-add"
            onClick={onCreateProject}
            disabled={disabled}
            aria-label="新建项目"
            title="新建项目"
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>

        {visibleProjects.map((project) => (
          <button
            type="button"
            key={project.id}
            className={`sidebar-item${project.id === activeProjectId ? " is-active" : ""}`}
            onClick={() => onSelectProject(project.id)}
            disabled={disabled}
            aria-current={project.id === activeProjectId ? "page" : undefined}
            title={project.title}
          >
            <Folder size={14} aria-hidden="true" />
            <span className="sidebar-item__name">{project.title}</span>
            {project.boardCount !== undefined && (
              <span className="sidebar-item__count">{project.boardCount}</span>
            )}
          </button>
        ))}

        {visibleProjects.length === 0 && (
          <div className="sidebar-item" role="status">
            <span className="sidebar-item__name">没有匹配的项目</span>
          </div>
        )}
      </section>

      <section
        className="sidebar-section is-scrollable"
        aria-labelledby="boards-heading"
      >
        <div
          className="sidebar-heading sidebar-board-root-drop"
          id="boards-heading"
          onDragOver={(event) => {
            if (event.dataTransfer.types.includes(BOARD_DRAG_MIME)) event.preventDefault();
          }}
          onDrop={(event) => acceptBoardDrop(event, null)}
          title="把画板拖到这里可移至顶层"
        >
          <span>画板</span>
          <button
            type="button"
            className="sidebar-add"
            onClick={onCreateBoard}
            disabled={disabled || !activeProjectId}
            aria-label="新建画板"
            title={activeProjectId ? "新建画板" : "请先选择项目"}
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>

        {boardRows.map(({ board, depth }) => (
          <div
            key={board.id}
            className="sidebar-board-row"
            data-board-id={board.id}
            style={{ "--board-depth": depth } as CSSProperties}
            draggable={!disabled}
            onDragStart={(event) => beginBoardDrag(event, board.id)}
            onDragOver={(event) => {
              if (event.dataTransfer.types.includes(BOARD_DRAG_MIME)) event.preventDefault();
            }}
            onDrop={(event) => acceptBoardDrop(event, board.id)}
          >
            <button
              type="button"
              className={`sidebar-item${board.id === activeBoardId ? " is-active" : ""}`}
              onClick={() => onSelectBoard(board.id)}
              disabled={disabled}
              aria-current={board.id === activeBoardId ? "page" : undefined}
              title={`${board.title}\nID: ${board.id}\n拖到其他画板上可修改父级`}
            >
              <PanelTop size={14} aria-hidden="true" />
              <span className="sidebar-item__name">{board.title}</span>
              {board.nodeCount !== undefined && (
                <span className="sidebar-item__count">{board.nodeCount}</span>
              )}
            </button>
            {onDuplicateBoard || onRequestMoveBoard || onRequestDeleteBoard ? (
              <button
                type="button"
                className="sidebar-board-action"
                onClick={(event) => toggleBoardMenu(event, board)}
                onDragStart={(event) => event.preventDefault()}
                disabled={disabled}
                aria-label={`画板操作 ${board.title}`}
                aria-haspopup="menu"
                aria-expanded={boardMenu?.board.id === board.id}
                title="更多画板操作"
              >
                <Ellipsis size={14} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ))}

        {visibleBoards.length === 0 && (
          <div className="sidebar-item" role="status">
            <span className="sidebar-item__name">
              {activeProjectId ? "没有匹配的画板" : "选择一个项目以查看画板"}
            </span>
          </div>
        )}
      </section>

      {hasFooter && (
        <footer className="sidebar-footer" aria-label="其他操作">
          {onImportProject && (
            <button type="button" onClick={onImportProject} disabled={disabled}>
              <Import size={15} aria-hidden="true" />
              <span>导入</span>
            </button>
          )}
          {onOpenSettings && (
            <button type="button" onClick={onOpenSettings} disabled={disabled}>
              <Settings size={15} aria-hidden="true" />
              <span>设置</span>
            </button>
          )}
          {onOpenHelp && (
            <button type="button" onClick={onOpenHelp} disabled={disabled}>
              <CircleHelp size={15} aria-hidden="true" />
              <span>帮助</span>
            </button>
          )}
        </footer>
      )}
    </aside>
    {boardMenu ? createPortal(
      <>
        <button
          type="button"
          className="sidebar-board-menu-scrim"
          tabIndex={-1}
          aria-label="关闭画板菜单"
          onClick={() => setBoardMenu(null)}
        />
        <div
          className="sidebar-board-menu"
          role="menu"
          aria-label={`${boardMenu.board.title} 的画板操作`}
          style={{ top: boardMenu.top, left: boardMenu.left }}
          onKeyDown={(event) => { if (event.key === "Escape") setBoardMenu(null); }}
        >
          <button type="button" role="menuitem" autoFocus onClick={() => { setBoardMenu(null); onRequestMoveBoard?.(boardMenu.board.id); }}>
            <Waypoints size={14} aria-hidden="true" />
            移动
          </button>
          <button type="button" role="menuitem" onClick={() => { setBoardMenu(null); onDuplicateBoard?.(boardMenu.board.id); }}>
            <Copy size={14} aria-hidden="true" />
            复制
          </button>
          <span className="sidebar-board-menu__divider" />
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() => { setBoardMenu(null); onRequestDeleteBoard?.(boardMenu.board.id); }}
            disabled={boards.length <= 1}
            title={boards.length <= 1 ? "项目至少需要保留一个画板" : "删除画板"}
          >
            <Trash2 size={14} aria-hidden="true" />
            删除
          </button>
        </div>
      </>,
      document.body,
    ) : null}
    </>
  );
}
